import { z } from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type WorkspaceAgentThreadCreatedLocation, type WorkspaceAgentThreadReopenedFrom } from '@air/analytics';
import { type Board } from '@air/api/types';

import { type WorkspaceAgentMarketingComposerDraft } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMarketingComposerDraft';
import { type WorkspaceAgentPanelFloatRect } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelFloatRect';
import { type WorkspaceAgentPanelView } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelView';
import { clampWorkspaceAgentPanelHeight } from '~/components/v2/components/workspace/workspace-agent-panel/utils/clampWorkspaceAgentPanelHeight';
import { getWorkspaceAgentPanelFloatWidthViewportBound } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelFloatHorizontalBounds';
import { syncWorkspaceAgentPanelWidthToCSS } from '~/components/v2/components/workspace/workspace-agent-panel/utils/syncWorkspaceAgentPanelWidthToCSS';
import { type WorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import {
  createWorkspaceAgentPanelOnboardingSlice,
  type WorkspaceAgentPanelOnboardingSlice,
} from '~/components/v2/store/features/workspace/slices/createWorkspaceAgentPanelOnboardingSlice';
import {
  createWorkspaceSidebarResizingSlice,
  type WorkspaceSidebarResizingSlice,
} from '~/components/v2/store/shared/createWorkspaceSidebarResizingSlice';
import {
  WORKSPACE_AGENT_PANEL_DEFAULT_FLOAT_HEIGHT,
  WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH,
  WORKSPACE_AGENT_PANEL_MAX_WIDTH,
  WORKSPACE_AGENT_PANEL_MIN_WIDTH,
  WORKSPACE_PANEL_EDGE_PADDING,
} from '~/constants/panelWidths';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';

const WORKSPACE_AGENT_PANEL_STORE_VERSION = 1;

/**
 * Docks that render in-flow and push the workspace content. Only `right` ships today; adding
 * `left`/`bottom` here is additive because `lastInFlowDock` is typed against this union.
 */
export type WorkspaceAgentInFlowDock = 'right';

/** Expanded placements `open()` can restore after a minimize — in-flow or the floating card. */
export type WorkspaceAgentExpandedDock = WorkspaceAgentInFlowDock | 'float';

export type WorkspaceAgentDock = WorkspaceAgentExpandedDock | 'minimized';

export const clampWorkspaceAgentPanelWidth = (width: number, maxWidth = WORKSPACE_AGENT_PANEL_MAX_WIDTH): number => {
  // `Math.min`/`Math.max` pass `NaN` straight through, which would persist as the panel width and
  // collapse it on the next load.
  if (!Number.isFinite(width)) {
    return WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH;
  }

  // Docked width still uses `WORKSPACE_AGENT_PANEL_MAX_WIDTH`. Float callers pass a viewport bound
  // (or Infinity on rehydrate) so that 400px cap does not block horizontal resize.
  const upperBound = Number.isFinite(maxWidth)
    ? Math.max(WORKSPACE_AGENT_PANEL_MIN_WIDTH, maxWidth)
    : Number.POSITIVE_INFINITY;

  return Math.min(upperBound, Math.max(WORKSPACE_AGENT_PANEL_MIN_WIDTH, width));
};

/**
 * Which affordance should take focus once it mounts. Minimizing and opening both unmount the
 * element that was focused, so without this focus falls to `<body>` and a keyboard user has to tab
 * in from the top of the page again. Not persisted — a reload should never move focus.
 */
export type WorkspaceAgentPanelPendingFocus = 'composer' | 'panel' | 'fab';

/**
 * Theme applied to the panel root via `data-theme`. The asset view bridge writes it so the panel
 * matches its host; cleared on unmount. Not persisted — the panel falls back to `'dark'` after a
 * reload, same as before the asset view opened.
 */
export type WorkspaceAgentPanelSurfaceTheme = 'dark' | 'light';

type WorkspaceAgentPanelFloatPlacementState = {
  /** Viewport `left` for `dock: 'float'`. Ignored while in-flow. */
  floatLeft: number;
  /** Viewport `top` for `dock: 'float'`. Ignored while in-flow. */
  floatTop: number;
  /**
   * Overlay card size. Independent of `width` so docking does not inherit a resized float, and
   * minimize → open / undock can restore the last detached size.
   */
  floatHeight: number;
  floatWidth: number;
  /** Where `open()` puts the panel back after a minimize (in-flow right or float). */
  lastExpandedDock: WorkspaceAgentExpandedDock;
};

type WorkspaceAgentPanelFloatPlacementActions = {
  /**
   * Undock into a floating card. Remembers float as the expanded placement so minimize → open
   * comes back as the overlay instead of the squeezed slot.
   */
  float: (rect: WorkspaceAgentPanelFloatRect) => void;
  setFloatGeometry: (rect: WorkspaceAgentPanelFloatRect) => void;
  dockToRight: () => void;
};

export type WorkspaceAgentBoardTemplatesFlowBoard = Pick<Board, 'id' | 'title' | 'workspaceId'>;

export type OpenAgentSessionInPanelOptions = {
  reopenedFrom?: WorkspaceAgentThreadReopenedFrom;
};

export type WorkspaceAgentPanelState = {
  /** `minimized` hides the panel; agent entry is header Start task, idle FAB, or the selection bar. */
  dock: WorkspaceAgentDock;
  /**
   * Docked width in px (ui2026 live layout reads `workspaceAgentPanelConstants` width CSS variable).
   */
  width: number;
  /**
   * Last in-flow edge. Independent of `lastExpandedDock` so a float minimize can restore the
   * overlay while still remembering which slot to squeeze if the user docks again.
   */
  lastInFlowDock: WorkspaceAgentInFlowDock;
  pendingFocus: WorkspaceAgentPanelPendingFocus | null;
  /**
   * Handshake for chrome outside `PrivateWorkspaceAgentPanelProvider` (header / selection-bar Start
   * task). The provider applies `resetSession` then clears this. Not persisted — a reload should
   * never throw away a restored `chatId`.
   */
  pendingSessionReset: boolean;
  /**
   * Automation detail sidebar "View all" → My Tasks runs subview. Consumed in
   * `PrivateWorkspaceAgentPanelMyTasks`. Not persisted.
   */
  pendingAutomationTasksFocusId: string | null;
  /**
   * Automation detail run row → open session in panel. Consumed in
   * `PrivateWorkspaceAgentPanelProvider`. Not persisted.
   */
  pendingAgentSessionId: string | null;
  /**
   * Attribution for `agent_thread_opened` when `pendingAgentSessionId` is consumed. Not persisted.
   */
  pendingAgentSessionReopenedFrom: WorkspaceAgentThreadReopenedFrom | null;
  /**
   * Hero seed from selection menus (outside `WorkspaceAgentComposerSeedProvider`). Consumed when the
   * panel is expanded — same lifecycle as marketing URL seed. Not persisted.
   */
  pendingHeroComposerSeed: WorkspaceAgentComposerSeed | null;
  /**
   * Board whose "Board templates" menu item opened this blank thread. The empty thread shows clickable
   * template choices for it instead of the usual suggestions. Not persisted.
   */
  boardTemplatesFlowBoard: WorkspaceAgentBoardTemplatesFlowBoard | null;
  /**
   * Attribution for `agent_thread_opened` (type `new`), set when the user starts a blank thread from
   * chrome and consumed on the first successful send that creates a server session. Not persisted.
   */
  pendingThreadCreatedLocation: WorkspaceAgentThreadCreatedLocation | null;
  /**
   * Marketing `agentUseCase` preload (WORK-664). Held until the mounted composer contains the
   * prompt so a TipTap remount after panel open cannot drop the draft. Not persisted.
   */
  marketingComposerDraft: WorkspaceAgentMarketingComposerDraft | null;
  surfaceTheme: WorkspaceAgentPanelSurfaceTheme | null;
  /**
   * Which view the panel shows. Store-level rather than panel-local because the asset view's
   * header button sits in a sibling subtree to the panel and so cannot reach it through context.
   * Not persisted — a reload should land on chat, and `PrivateWorkspaceAgentPanelProvider` resets it
   * on unmount so it does not outlive the workspace that provider is keyed to.
   */
  panelView: WorkspaceAgentPanelView;

  setWidth: (width: number) => void;
  minimize: (pendingFocus?: WorkspaceAgentPanelPendingFocus | null) => void;
  open: () => void;
  /**
   * Open chat on a new thread. Unlike `open()`, this still runs when the panel is already
   * expanded — `open()` no-ops then, which is why Start Task used to keep the previous chat.
   */
  startNewThread: (location: WorkspaceAgentThreadCreatedLocation) => void;
  startNewThreadWithHeroSeed: (location: WorkspaceAgentThreadCreatedLocation, seed: WorkspaceAgentComposerSeed) => void;
  startNewThreadWithBoardTemplates: (
    location: WorkspaceAgentThreadCreatedLocation,
    board: WorkspaceAgentBoardTemplatesFlowBoard,
  ) => void;
  clearBoardTemplatesFlowBoard: () => void;
  setPendingHeroComposerSeed: (seed: WorkspaceAgentComposerSeed | null) => void;
  clearPendingHeroComposerSeed: () => void;
  clearPendingSessionReset: () => void;
  clearPendingAutomationTasksFocusId: () => void;
  clearPendingAgentSessionId: () => void;
  openAutomationRunsInTasks: (automationId: string) => void;
  openAgentSessionInPanel: (sessionId: string, options?: OpenAgentSessionInPanelOptions) => void;
  clearPendingThreadCreatedLocation: () => void;
  /** Drops `onboarding` only. A later New thread's location stays. */
  clearOnboardingThreadCreatedLocation: () => void;
  consumePendingThreadCreatedLocation: () => WorkspaceAgentThreadCreatedLocation | null;
  setPendingThreadCreatedLocation: (location: WorkspaceAgentThreadCreatedLocation) => void;
  setMarketingComposerDraft: (draft: WorkspaceAgentMarketingComposerDraft | null) => void;
  clearMarketingComposerDraft: () => void;
  // Lands focus on the composer after a non-minimize action (e.g. the
  // header's `New session` reset). Kept distinct from `open`, which only runs
  // when the panel is being un-minimized.
  focusComposer: () => void;
  clearPendingFocus: () => void;
  setSurfaceTheme: (surfaceTheme: WorkspaceAgentPanelSurfaceTheme | null) => void;
  /** Switch views without changing dock or pendingFocus. */
  setPanelView: (panelView: WorkspaceAgentPanelView) => void;
  toggleTasks: () => void;
  /** Open chat and request composer focus. Use `setPanelView('chat')` when focus should stay put. */
  showChat: (options?: { focusComposer?: boolean }) => void;
  /**
   * True after the user explicitly removes board scope from the composer or clears the agent
   * selection bar. Not persisted — board navigation and new threads reset it.
   */
  isComposerBoardScopeOptedOut: boolean;
  optOutComposerBoardScope: () => void;
  resetComposerBoardScopeOptOut: () => void;
  /**
   * True after the user removes asset scope from the composer while the asset modal is open.
   * Resets when the viewed clip changes or the modal closes.
   */
  isComposerAssetViewScopeOptedOut: boolean;
  optOutComposerAssetViewScope: () => void;
  resetComposerAssetViewScopeOptOut: () => void;
  /** Resets board and asset-modal composer scope opt-outs (new thread / session switch). */
  resetComposerScopeOptOuts: () => void;
} & WorkspaceAgentPanelFloatPlacementState &
  WorkspaceAgentPanelFloatPlacementActions &
  WorkspaceAgentPanelOnboardingSlice &
  WorkspaceSidebarResizingSlice;

const getFloatHeightViewportBound = () =>
  typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerHeight;

const getFloatWidthViewportBound = () =>
  typeof window === 'undefined'
    ? Number.POSITIVE_INFINITY
    : getWorkspaceAgentPanelFloatWidthViewportBound(window.innerWidth);

export const isWorkspaceAgentPanelSlotOpen = ({ dock }: Pick<WorkspaceAgentPanelState, 'dock'>): boolean =>
  dock === 'right';

export const isWorkspaceAgentPanelExpanded = ({ dock }: Pick<WorkspaceAgentPanelState, 'dock'>): boolean =>
  dock !== 'minimized';

const rememberExpandedDock = (
  dock: WorkspaceAgentDock,
  lastExpandedDock: WorkspaceAgentExpandedDock,
): WorkspaceAgentExpandedDock => (dock === 'minimized' ? lastExpandedDock : dock);

const rememberInFlowDock = (
  dock: WorkspaceAgentDock,
  lastInFlowDock: WorkspaceAgentInFlowDock,
): WorkspaceAgentInFlowDock => (dock === 'right' ? dock : lastInFlowDock);

/**
 * Persisted blob is untrusted: a stale or hand-edited `localStorage` entry would otherwise
 * flow straight into `dock` and render a position the panel has no branch for.
 */
const PersistedWorkspaceAgentPanelStateSchema = z.object({
  dock: z.enum(['right', 'float', 'minimized']).default('minimized'),
  width: z.number().default(WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH),
  floatLeft: z.number().default(WORKSPACE_PANEL_EDGE_PADDING),
  floatTop: z.number().default(WORKSPACE_PANEL_EDGE_PADDING),
  floatHeight: z.number().default(WORKSPACE_AGENT_PANEL_DEFAULT_FLOAT_HEIGHT),
  floatWidth: z.number().optional(),
  lastExpandedDock: z.enum(['right', 'float']).default('right'),
  lastInFlowDock: z.enum(['right']).default('right'),
});

const parsePersistedWorkspaceAgentPanelState = (persistedState: unknown) => {
  const parsed = PersistedWorkspaceAgentPanelStateSchema.safeParse(persistedState);
  const state = parsed.success ? parsed.data : PersistedWorkspaceAgentPanelStateSchema.parse({});
  // Older blobs wrote overlay width onto `width`. Keep that size as the detached card when
  // `floatWidth` has never been stored on its own.
  const floatWidthSource = state.floatWidth ?? state.width;

  return {
    ...state,
    floatHeight: clampWorkspaceAgentPanelHeight(state.floatHeight),
    floatWidth: clampWorkspaceAgentPanelWidth(floatWidthSource, Number.POSITIVE_INFINITY),
    lastExpandedDock: state.dock === 'minimized' ? state.lastExpandedDock : state.dock,
    width: clampWorkspaceAgentPanelWidth(state.width),
  };
};

export const useWorkspaceAgentPanelStore = create<WorkspaceAgentPanelState>()(
  persist(
    (set, get, store) => ({
      ...createWorkspaceAgentPanelOnboardingSlice()(set, get, store),
      ...createWorkspaceSidebarResizingSlice()(set, get, store),
      dock: 'minimized',
      width: WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH,
      floatLeft: WORKSPACE_PANEL_EDGE_PADDING,
      floatTop: WORKSPACE_PANEL_EDGE_PADDING,
      floatHeight: WORKSPACE_AGENT_PANEL_DEFAULT_FLOAT_HEIGHT,
      floatWidth: WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH,
      lastExpandedDock: 'right',
      lastInFlowDock: 'right',
      pendingFocus: null,
      pendingSessionReset: false,
      boardTemplatesFlowBoard: null,
      pendingAutomationTasksFocusId: null,
      pendingAgentSessionId: null,
      pendingAgentSessionReopenedFrom: null,
      pendingHeroComposerSeed: null,
      pendingThreadCreatedLocation: null,
      marketingComposerDraft: null,
      surfaceTheme: null,
      panelView: 'chat',
      isComposerBoardScopeOptedOut: false,
      isComposerAssetViewScopeOptedOut: false,

      setWidth: (width) => {
        const clamped = clampWorkspaceAgentPanelWidth(width);
        syncWorkspaceAgentPanelWidthToCSS(clamped);
        set({ width: clamped });
      },

      optOutComposerBoardScope: () => set({ isComposerBoardScopeOptedOut: true }),

      resetComposerBoardScopeOptOut: () => set({ isComposerBoardScopeOptedOut: false }),

      optOutComposerAssetViewScope: () => set({ isComposerAssetViewScopeOptedOut: true }),

      resetComposerAssetViewScopeOptOut: () => set({ isComposerAssetViewScopeOptedOut: false }),

      resetComposerScopeOptOuts: () =>
        set({
          isComposerAssetViewScopeOptedOut: false,
          isComposerBoardScopeOptedOut: false,
          marketingComposerDraft: null,
        }),

      float: (rect) => {
        set({
          dock: 'float',
          floatHeight: clampWorkspaceAgentPanelHeight(rect.height, getFloatHeightViewportBound()),
          floatLeft: rect.left,
          floatTop: rect.top,
          floatWidth: clampWorkspaceAgentPanelWidth(rect.width, getFloatWidthViewportBound()),
          lastExpandedDock: 'float',
        });
      },

      setFloatGeometry: (rect) => {
        set({
          floatHeight: clampWorkspaceAgentPanelHeight(rect.height, getFloatHeightViewportBound()),
          floatLeft: rect.left,
          floatTop: rect.top,
          floatWidth: clampWorkspaceAgentPanelWidth(rect.width, getFloatWidthViewportBound()),
        });
      },

      dockToRight: () => {
        const { dock } = get();

        if (dock === 'right') {
          return;
        }

        set({ dock: 'right', lastExpandedDock: 'right', lastInFlowDock: 'right' });
      },

      minimize: (pendingFocus = 'fab') => {
        const { dock, lastExpandedDock, lastInFlowDock } = get();

        if (dock === 'minimized') {
          return;
        }

        set({
          dock: 'minimized',
          lastExpandedDock: rememberExpandedDock(dock, lastExpandedDock),
          lastInFlowDock: rememberInFlowDock(dock, lastInFlowDock),
          panelView: 'chat',
          pendingFocus,
        });
      },

      open: () => {
        const { dock, lastExpandedDock } = get();

        if (dock !== 'minimized') {
          return;
        }

        set({ dock: lastExpandedDock, pendingFocus: 'composer' });
      },

      clearPendingSessionReset: () => {
        if (!get().pendingSessionReset) {
          return;
        }

        set({ pendingSessionReset: false });
      },

      clearPendingAutomationTasksFocusId: () => {
        if (get().pendingAutomationTasksFocusId === null) {
          return;
        }

        set({ pendingAutomationTasksFocusId: null });
      },

      clearPendingAgentSessionId: () => {
        if (get().pendingAgentSessionId === null) {
          return;
        }

        set({ pendingAgentSessionId: null, pendingAgentSessionReopenedFrom: null });
      },

      openAutomationRunsInTasks: (automationId) => {
        const { dock, lastExpandedDock } = get();

        set({
          dock: dock === 'minimized' ? lastExpandedDock : dock,
          panelView: 'tasks',
          pendingAutomationTasksFocusId: automationId,
          pendingFocus: 'panel',
        });
      },

      openAgentSessionInPanel: (sessionId, options) => {
        const { dock, lastExpandedDock } = get();

        set({
          dock: dock === 'minimized' ? lastExpandedDock : dock,
          panelView: 'chat',
          pendingAgentSessionId: sessionId,
          pendingAgentSessionReopenedFrom: options?.reopenedFrom ?? null,
          pendingFocus: 'panel',
        });
      },

      clearPendingThreadCreatedLocation: () => {
        if (get().pendingThreadCreatedLocation === null) {
          return;
        }

        set({ pendingThreadCreatedLocation: null });
      },

      clearOnboardingThreadCreatedLocation: () => {
        if (get().pendingThreadCreatedLocation !== 'onboarding') {
          return;
        }

        set({ pendingThreadCreatedLocation: null });
      },

      consumePendingThreadCreatedLocation: () => {
        const location = get().pendingThreadCreatedLocation;

        if (location === null) {
          return null;
        }

        set({ pendingThreadCreatedLocation: null });

        return location;
      },

      setPendingThreadCreatedLocation: (location) => set({ pendingThreadCreatedLocation: location }),

      setMarketingComposerDraft: (draft) => set({ marketingComposerDraft: draft }),

      clearMarketingComposerDraft: () => {
        if (get().marketingComposerDraft === null) {
          return;
        }

        set({ marketingComposerDraft: null });
      },

      focusComposer: () => set({ pendingFocus: 'composer' }),

      clearPendingFocus: () => set({ pendingFocus: null }),

      setSurfaceTheme: (surfaceTheme) => {
        if (surfaceTheme === get().surfaceTheme) {
          return;
        }

        set({ surfaceTheme });
      },

      setPanelView: (panelView) => set({ panelView }),

      toggleTasks: () => {
        const { dock, lastExpandedDock, lastInFlowDock, panelView } = get();

        if (dock !== 'minimized' && panelView === 'tasks') {
          // Back to chat as well as minimized, because `open()` asks for composer focus and the
          // tasks view renders no composer. A `tasks` view left behind here would strand that
          // request — nothing would mount to consume it or call `clearPendingFocus`, so it would
          // fire whenever the user next reached chat.
          set({
            dock: 'minimized',
            lastExpandedDock: rememberExpandedDock(dock, lastExpandedDock),
            lastInFlowDock: rememberInFlowDock(dock, lastInFlowDock),
            panelView: 'chat',
            pendingFocus: null,
          });

          return;
        }

        // `pendingFocus: 'panel'` lands keyboard focus in the portalled panel when the asset header's
        // Tasks button opens it — without this, tab order runs through the modal first. Minimizing
        // from tasks leaves focus on the header button, which stays mounted.
        set({
          dock: dock === 'minimized' ? lastExpandedDock : dock,
          panelView: 'tasks',
          pendingFocus: 'panel',
        });
      },

      showChat: (options) => {
        const { dock, lastExpandedDock } = get();
        const focusComposer = options?.focusComposer !== false;

        set({
          dock: dock === 'minimized' ? lastExpandedDock : dock,
          panelView: 'chat',
          pendingFocus: focusComposer ? 'composer' : null,
        });
      },

      startNewThread: (location) => {
        // The intro's simulated thread lives in memory, not in the session. A new thread drops it.
        get().setOnboardingHandoff(null);
        get().showChat();
        set({
          boardTemplatesFlowBoard: null,
          pendingSessionReset: true,
          pendingThreadCreatedLocation: location,
          pendingHeroComposerSeed: null,
        });
      },

      startNewThreadWithHeroSeed: (location, seed) => {
        get().showChat();
        set({
          boardTemplatesFlowBoard: null,
          pendingHeroComposerSeed: seed,
          pendingSessionReset: true,
          pendingThreadCreatedLocation: location,
        });
      },

      startNewThreadWithBoardTemplates: (location, board) => {
        get().setOnboardingHandoff(null);
        get().showChat({ focusComposer: false });
        set({
          boardTemplatesFlowBoard: board,
          pendingHeroComposerSeed: null,
          pendingSessionReset: true,
          pendingThreadCreatedLocation: location,
        });
      },

      clearBoardTemplatesFlowBoard: () => {
        if (get().boardTemplatesFlowBoard === null) {
          return;
        }

        set({ boardTemplatesFlowBoard: null });
      },

      setPendingHeroComposerSeed: (seed) => set({ pendingHeroComposerSeed: seed }),

      clearPendingHeroComposerSeed: () => {
        if (get().pendingHeroComposerSeed === null) {
          return;
        }

        set({ pendingHeroComposerSeed: null });
      },
    }),
    {
      name: ZustandStorageKeys.WorkspaceAgentPanel,
      partialize: (state) => ({
        dock: state.dock,
        width: state.width,
        floatLeft: state.floatLeft,
        floatTop: state.floatTop,
        floatHeight: state.floatHeight,
        floatWidth: state.floatWidth,
        lastExpandedDock: state.lastExpandedDock,
        lastInFlowDock: state.lastInFlowDock,
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...parsePersistedWorkspaceAgentPanelState(persistedState),
      }),
      version: WORKSPACE_AGENT_PANEL_STORE_VERSION,
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) {
          return;
        }

        syncWorkspaceAgentPanelWidthToCSS(state.width);
      },
    },
  ),
);

useWorkspaceAgentPanelStore.subscribe((state, previousState) => {
  if (state.width === previousState.width) {
    return;
  }

  syncWorkspaceAgentPanelWidthToCSS(state.width);
});

if (typeof window !== 'undefined') {
  const syncWidthFromStore = () => {
    syncWorkspaceAgentPanelWidthToCSS(useWorkspaceAgentPanelStore.getState().width);
  };

  syncWidthFromStore();
  useWorkspaceAgentPanelStore.persist.onFinishHydration(syncWidthFromStore);
}
